import {
  ArrowTopRightOnSquareIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  BeakerIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  CheckBadgeIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { serviceColors } from "../../utils/constants";
import VendorServiceCategoryBadges from "../VendorServiceCategoryBadges";
import { formatVqqName } from "../../utils/vqqNameFormat";
import JSZip from "jszip";
import moment from "moment";
import useChatDQP from "../../custom-hooks/useChatDQP";
import { useDispatch } from "react-redux";
import { setVendorsFilters } from "../../redux-store/features/indicationsDashboardFiltersSlice";
import {
  downloadExportDocs,
  downloadExportFile,
  exportQuestionnaireExcel,
  getRFIsDetails,
} from "../../api/api-methods";
import { useDownloadExportFile } from "../../custom-hooks/queries";

// Vendor type color dots (matching the platform's color scheme)
const vendorTypeDotColors = {
  CDMO: "bg-yellow-300",
  CRO: "bg-green-400",
  "Medical Device": "bg-blue-600",
  "Service/Technology Provider": "bg-purple-700",
  Other: "bg-red-500",
};

// Vendor type color hex values for inline styles
const vendorTypeHexColors = {
  CDMO: "#FCD34D",
  CRO: "#4ADE80",
  "Medical Device": "#2563EB",
  "Service/Technology Provider": "#7E22CE",
  Other: "#EF4444",
};

// Vendor Profile Card Preview - styled like vendor profile headers
export const VendorProfileCard = ({
  vendor,
  highlightTerms = [],
  badgeType = "therapeuticAreas",
  onClose,
}) => {
  const navigate = useNavigate();

  // Add "late phase" to highlight terms if Phase III is mentioned
  const expandedHighlightTerms = [...highlightTerms];
  if (
    highlightTerms.some(
      (t) =>
        t.toLowerCase().includes("phase iii") ||
        t.toLowerCase().includes("phase 3")
    )
  ) {
    expandedHighlightTerms.push("late phase", "late-phase", "late-stage");
  }

  const dotColor = vendorTypeDotColors[vendor.vendorType] || "bg-gray-400";

  // Helper to display bio with highlighted relevant terms
  const renderBioQuote = (bio) => {
    if (!bio) return null;

    const lowerBio = bio.toLowerCase();
    const lowerTerms = expandedHighlightTerms.map((t) => t.toLowerCase());

    // Check if any highlight terms exist in bio
    let hasMatch = false;
    let matchedTerm = null;
    for (const term of expandedHighlightTerms) {
      if (lowerBio.includes(term.toLowerCase())) {
        hasMatch = true;
        matchedTerm = term;
        break;
      }
    }

    // If there's a match, show the relevant sentence with inline highlighting
    if (hasMatch && matchedTerm) {
      const regex = new RegExp(`([^.]*${matchedTerm}[^.]*)`, "i");
      const match = bio.match(regex);
      if (match) {
        const snippet = match[0].trim();
        // Split by all possible highlight terms
        let highlightedSnippet = snippet;
        expandedHighlightTerms.forEach((term) => {
          const termRegex = new RegExp(`(${term})`, "gi");
          highlightedSnippet = highlightedSnippet.replace(
            termRegex,
            "|||HIGHLIGHT_START|||$1|||HIGHLIGHT_END|||"
          );
        });

        // Split and render with highlights
        const parts = highlightedSnippet.split("|||");
        return (
          <p className="text-xs text-gray-600 italic mt-2">
            "
            {parts.map((part, i) => {
              if (part === "HIGHLIGHT_START") return null;
              if (part === "HIGHLIGHT_END") return null;

              // Check if the previous part was HIGHLIGHT_START
              const isHighlighted = i > 0 && parts[i - 1] === "HIGHLIGHT_START";

              if (isHighlighted) {
                return (
                  <strong
                    key={i}
                    className="text-[#6036C4] font-semibold not-italic"
                  >
                    {part}
                  </strong>
                );
              }
              return <span key={i}>{part}</span>;
            })}
            "
          </p>
        );
      }
    }

    // If no match, show first sentence of bio
    const firstSentence = bio.split(".")[0] + ".";
    return (
      <p className="text-xs text-gray-600 italic mt-2">"{firstSentence}"</p>
    );
  };

  const handleViewProfile = (e) => {
    e.stopPropagation(); // Prevent card click
    if (onClose) onClose();
    setTimeout(() => {
      navigate(`/provider/${vendor.id}/overview`);
      window.scrollTo(0, 0);
    }, 100); // Small delay to allow drawer close animation
  };

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 hover:border-[#6036C4] hover:shadow-lg transition-all group">
      <div className="flex items-start gap-3">
        {vendor.logo && (
          <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
            <img
              src={vendor.logo}
              alt={vendor.name}
              className="w-10 h-10 object-contain"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1.5">
                {vendor.name}
              </h4>
              <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                <span className="flex items-center gap-1.5">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${dotColor}`}
                  />
                  {vendor.vendorType}
                </span>
                {vendor.location && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="flex items-center gap-1">
                      <BuildingOfficeIcon className="h-3 w-3" />
                      {vendor.location}
                    </span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={handleViewProfile}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors shadow-sm"
              style={{ backgroundColor: "#6036C4" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#5028b0")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#6036C4")
              }
            >
              View Profile
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Conditional Badges: Service Categories OR Therapeutic Areas */}
          {badgeType === "serviceCategories"
            ? // Show Service Categories
              vendor.serviceCategories &&
              vendor.serviceCategories.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {vendor.serviceCategories.slice(0, 4).map((cat, idx) => {
                    const isHighlighted = expandedHighlightTerms.some((term) =>
                      cat.toLowerCase().includes(term.toLowerCase())
                    );
                    const serviceColor = serviceColors[cat];
                    // Handle both old string format and new object format
                    const colorStyle = typeof serviceColor === 'object' && serviceColor?.bgColor
                      ? {
                          backgroundColor: serviceColor.bgColor,
                          color: serviceColor.textColor,
                          border: `1px solid ${serviceColor.borderColor}`,
                        }
                      : {};
                    return (
                      <span
                        key={idx}
                        style={isHighlighted ? {} : colorStyle}
                        className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ${
                          isHighlighted
                            ? "bg-purple-100 text-[#6036C4] ring-1 ring-inset ring-[#6036C4]"
                            : typeof serviceColor === 'object' && serviceColor?.bgColor
                            ? ""
                            : `ring-1 ring-inset ${serviceColor?.bg || ''} ${serviceColor?.text || ''} ${serviceColor?.ring || ''}`
                        }`}
                      >
                        {cat}
                      </span>
                    );
                  })}
                </div>
              )
            : // Show Therapeutic Areas (default)
              vendor.therapeuticAreas &&
              vendor.therapeuticAreas.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {vendor.therapeuticAreas.slice(0, 3).map((ta, idx) => {
                    const taName = typeof ta === "string" ? ta : ta.name;
                    const isHighlighted = expandedHighlightTerms.some((term) =>
                      taName.toLowerCase().includes(term.toLowerCase())
                    );
                    return (
                      <span
                        key={idx}
                        className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                          isHighlighted
                            ? "bg-purple-100 text-[#6036C4] ring-[#6036C4]"
                            : "bg-white text-gray-900 ring-gray-300"
                        }`}
                      >
                        {taName}
                      </span>
                    );
                  })}
                </div>
              )}

          {/* Bio quote with highlighted relevant terms */}
          {renderBioQuote(vendor.bio)}
        </div>
      </div>
    </div>
  );
};

// RFI Response Preview Card
export const RFIResponseCard = ({ vendor, rfiTitle, onClose }) => {
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (onClose) onClose();
    setTimeout(() => {
      navigate("/documents/RFI");
    }, 100);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 hover:border-[#6036C4] transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <BuildingOfficeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <h4 className="text-sm font-semibold text-gray-900">{vendor}</h4>
          </div>
          <p className="text-xs text-gray-600 mb-2">{rfiTitle}</p>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
              Complete
            </span>
            <span className="text-xs text-gray-500">
              Last updated 2 weeks ago
            </span>
          </div>
        </div>
      </div>
      <button
        onClick={handleNavigate}
        className="mt-2 w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors"
        style={{ backgroundColor: "#6036C4" }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#5028b0")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "#6036C4")
        }
      >
        View VQQ Response
        <ArrowTopRightOnSquareIcon className="h-3 w-3" />
      </button>
    </div>
  );
};

// RFI Comparison Card
export const RFIComparisonCard = ({
  templateName,
  vendors,
  question,
  responses,
  summary,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleViewRFI = (vendor) => {
    if (onClose) onClose();
    setTimeout(() => {
      navigate(`/provider/${vendor.vendorId}/overview`);
    }, 100);
  };

  const handleViewMyRFI = (rfi) => {
    if (onClose) onClose();
    setTimeout(() => {
      navigate(`/RFI/${rfi.rfiId}/Details`);
    }, 100);
  };

  const handleViewComparison = (responses) => {
    if (onClose) onClose();
    dispatch(
      setVendorsFilters({
        providerIds: responses?.map((d) => d.vendorId) ?? [],
      })
    );
    setTimeout(() => {
      navigate(`/TrialExperience`);
    }, 300);
  };

  return (
    <>
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 p-2 bg-white rounded-lg">
            <DocumentTextIcon className="h-5 w-5 text-[#6036C4]" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              {templateName}
            </h4>
            <p className="text-xs text-gray-600 mb-3 italic">"{question}"</p>
          </div>
        </div>

        {/* Vendor Responses */}
        <div className="space-y-3 mb-4">
          {responses.map((response, idx) => (
            <div
              key={idx}
              className="bg-white rounded-md p-3 border border-indigo-100"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <button
                  onClick={() => handleViewRFI(response)}
                  className="text-sm font-semibold text-[#6036C4] hover:text-[#5028b0] hover:underline"
                >
                  {response.vendorName}
                </button>
                <button
                  onClick={() => handleViewMyRFI(response)}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-md transition-colors"
                  style={{ backgroundColor: "#6036C4" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#5028b0")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6036C4")
                  }
                >
                  View My VQQ
                  <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                </button>
              </div>
              <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                "{response.response}"
              </p>
            </div>
          ))}
        </div>

        {/* TODO: Action Button */}
        {/* <button
          onClick={() => handleViewComparison(responses)}
          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-md transition-colors shadow-sm"
          style={{ backgroundColor: "#6036C4" }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#5028b0")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#6036C4")
          }
        >
          View Full Comparison
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </button> */}
      </div>

      {/* AI Summary as plain text response */}
      <div className="mt-3 text-sm text-gray-800 leading-relaxed">
        {summary}
      </div>
    </>
  );
};

// Compare Vendors Action Button
export const CompareVendorsAction = ({
  vendors,
  therapeuticArea,
  indication,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleCompare = () => {
    if (onClose) onClose();
    setTimeout(() => {
      // Navigate to compare screen with pre-populated vendors
      navigate("/Provider/Compare", {
        state: {
          preSelectedVendors: vendors,
          therapeuticArea,
          indication,
        },
      });
    }, 100);
  };

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-2 bg-white rounded-lg">
          <BeakerIcon className="h-5 w-5 text-[#6036C4]" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">
            Compare Clinical Experience
          </h4>
          <p className="text-xs text-gray-600 mb-3">
            View {vendors.length} vendors' experience in {therapeuticArea}{" "}
            {indication ? `- ${indication}` : ""}
          </p>
          <button
            onClick={handleCompare}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-md transition-colors shadow-sm"
            style={{ backgroundColor: "#6036C4" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#5028b0")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#6036C4")
            }
          >
            Open Comparison
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Compare Vendors with Summary
export const CompareVendorsWithSummary = ({
  vendors,
  therapeuticArea,
  title,
  summary,
  comparison,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleCompare = () => {
    if (!vendors?.length || !comparison || Object.keys(comparison).length === 0) return;
    // Close drawer first
    if (onClose) onClose();

    // Navigate after a brief delay to let drawer close animation start
    // TODO: set vendorIds, TAs and indications in state for comparison view
    dispatch(setVendorsFilters({ providerIds: vendors ?? [] }));
    setTimeout(() => {
      navigate(`/TrialExperience`);
    }, 300);
  };

  const handleVendorClick = (vendor) => {
    if (onClose) onClose();
    setTimeout(() => {
      navigate(`/provider/${vendor.id}/overview`);
      window.scrollTo(0, 0);
    }, 100);
  };

  return (
    <>
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-white rounded-lg">
            <BeakerIcon className="h-5 w-5 text-[#6036C4]" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">
              {title}
            </h4>

            {/* Comparison Summary */}
            {comparison && Object.keys(comparison).length > 0 ? (
              <div className="space-y-3 mb-4">
                {Object.entries(comparison).map(([vendorName, vendor]) => {
                return (
                  <div
                    key={vendorName}
                    className="bg-white rounded-md p-3 border border-indigo-100"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      {/* Vendor Logo */}
                      {vendor?.logo && (
                        <div className="flex-shrink-0 p-2 bg-white rounded-lg border border-gray-200 shadow-sm">
                          <img
                            src={vendor.logo}
                            alt={vendorName}
                            className="w-8 h-8 object-contain"
                          />
                        </div>
                      )}

                      {/* Vendor Name and Stats */}
                      <div className="flex-1">
                        <button
                          onClick={() => handleVendorClick(vendor)}
                          className="text-sm font-semibold text-[#6036C4] hover:text-[#5028b0] hover:underline mb-2 block"
                        >
                          {vendorName}
                        </button>

                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <p className="text-xs text-gray-500">Trials</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {vendor?.trials ?? 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Patients</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {(vendor?.patients ?? 0).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Indications</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {Array.isArray(vendor?.indications)
                                ? vendor.indications.length
                                : 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        Key Indications:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(vendor?.indications)
                          ? vendor.indications
                          : [])
                          .slice(0, 4)
                          .map((indication, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                            >
                              {indication}
                            </span>
                          ))}
                        {Array.isArray(vendor?.indications) &&
                          vendor.indications.length > 4 && (
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            +{vendor.indications.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-md p-3 border border-indigo-100 text-xs text-gray-600 mb-4">
                No trial experience data found for the requested vendors.
              </div>
            )}

            <button
              onClick={handleCompare}
              disabled={!vendors?.length || !comparison || Object.keys(comparison).length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#6036C4" }}
              onMouseEnter={(e) =>
                !e.currentTarget.disabled &&
                (e.currentTarget.style.backgroundColor = "#5028b0")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#6036C4")
              }
            >
              View Full Comparison
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <div className="mt-3 text-sm text-gray-800 leading-relaxed">
        {summary}
      </div>
    </>
  );
};

// Bulk Download Action
export const BulkDownloadAction = ({ rfis, onClose }) => {
  const navigate = useNavigate();
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestingRfi, setRequestingRfi] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [requestedRfis, setRequestedRfis] = useState(new Set());
  const { setOpen } = useChatDQP();

  const extractFilenameFromUrl = (url) => {
    try {
      // Remove query parameters and get the pathname
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;

      // Extract filename from pathname
      const filename = pathname
        .split("/")
        .pop()
        .replace(".pdf", "-" + new Date().getTime() + ".pdf");

      // Decode URL-encoded characters
      return decodeURIComponent(filename);
    } catch (error) {
      console.error("Error extracting filename from URL:", error);
      return "file.pdf"; // Fallback filename
    }
  };

  const handleDownload = async () => {
    // Only download complete RFIs
    // const completeRfis = rfis.filter((rfi) => rfi.status === "Complete");
    const completeRfis = rfis.filter((rfi) => rfi.status === "Complete");
    console.log("Downloading complete RFIs:", completeRfis);

    try {
      const zip = new JSZip();
      const fileWorkers = await Promise.all(
        completeRfis.map((rfi) =>
          getRFIsDetails(rfi.id).then((rfisDetails) =>
            exportQuestionnaireExcel({
              entity_id: rfisDetails?.questionnaire_id,
              entity_type: "QuestionnaireResponse",
              purchase_vendor: rfisDetails?.vendorName,
              sponsor_name: rfisDetails?.sponsorName,
              export_format: "pdf",
              org_id: rfisDetails?.organizationId,
              questionnaire_name: encodeURIComponent(rfisDetails.name),
              released_date: moment().toISOString(),
              subtype: "test",
            }).then((exportResponse) =>
              // BE returns file_name with conditional "{template} - state, country"
              // suffix for multi-location vendors — must match what the worker wrote.
              downloadExportFile(
                rfisDetails.questionnaire_id,
                `${exportResponse?.file_name || formatVqqName(rfisDetails.name) || "export"}.pdf`
              )
            )
          )
        )
      );

      const promises = fileWorkers.map((data) =>
        fetch(data.download_link)
          .then(
            (res) =>
              new Promise((resolve) =>
                setTimeout(() => resolve(res.blob()), 300)
              )
          )
          .then((blob) => {
            const filename = extractFilenameFromUrl(data.download_link);
            zip.file(filename, blob);
          })
      );

      Promise.all(promises).then(() => {
        zip.generateAsync({ type: "blob" }).then((zipBlob) => {
          const zipLink = document.createElement("a");
          zipLink.href = URL.createObjectURL(zipBlob);
          zipLink.setAttribute(
            "download",
            `DiligentPharmaVQQs-${moment().format("MM-DD-YYYY")}.zip`
          );
          document.body.appendChild(zipLink);
          zipLink.click();
          document.body.removeChild(zipLink);
        });
      });
    } catch (error) {
      // ZIP download failed
    }
  };

  const handleViewMyRFI = (rfi) => {
    if (onClose) onClose();
    setTimeout(() => {
      navigate(`/RFI/${rfi.id}/Details`);
    }, 100);
  };

  const handleRequestRFI = (rfi) => {
    // setRequestingRfi(rfi);
    // setShowRequestDialog(true);
    navigate(`/RFI/Create?id=${rfi.vendorId}&templateId=${rfi?.templateId}`);
    setOpen(false);
  };

  const handleConfirmRequest = async () => {
    setIsConfirming(true);
    try {
      console.log("Requesting RFI:", requestingRfi);
      // Simulate request
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Mark this RFI as requested
      const rfiKey = `${requestingRfi.vendor}-${requestingRfi.name}`;
      setRequestedRfis((prev) => new Set([...prev, rfiKey]));

      // Success - close dialog
      setShowRequestDialog(false);
      setRequestingRfi(null);
    } finally {
      setIsConfirming(false);
    }
  };

  const completeRfisCount = rfis.filter(
    (rfi) => rfi.status === "Complete"
  ).length;

  return (
    <>
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-white rounded-lg">
            <DocumentArrowDownIcon className="h-5 w-5 text-[#6036C4]" />
          </div>
          <div className="flex-1 w-[50%]">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">
              Available VQQs ({rfis.length})
            </h4>

            {/* List of RFIs */}
            <div className="space-y-2 mb-4">
              {rfis.map((rfi, idx) => {
                const rfiKey = `${rfi.vendor}-${rfi.name}`;
                const wasRequested = requestedRfis.has(rfiKey);
                const displayStatus =
                  rfi.status === "Published" && wasRequested
                    ? "Sent"
                    : rfi.status;
                const showRequestButton =
                  rfi.status === "Published" && !wasRequested;

                return (
                  <div
                    key={idx}
                    className="bg-white rounded-md px-3 py-2 border border-indigo-100"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex-1 min-w-0 w-full">
                        <p className="text-xs font-medium text-gray-900 break-words">
                          {rfi.vendor}
                        </p>
                        <p className="text-xs text-gray-600 break-words whitespace-normal sm:truncate">
                          {rfi.name}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                            displayStatus === "Complete"
                              ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20"
                              : displayStatus === "Published"
                              ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20"
                              : displayStatus === "Sent"
                              ? "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20"
                              : displayStatus === "In Progress"
                              ? "bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20"
                              : displayStatus === "In Review"
                              ? "bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20"
                              : "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20"
                          }`}
                        >
                          {displayStatus}
                        </span>
                        {showRequestButton ? (
                          <button
                            onClick={() => handleRequestRFI(rfi)}
                            className="inline-flex w-full sm:w-auto items-center justify-center gap-1 px-2 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                          >
                            Request VQQ
                          </button>
                        ) : (
                          <button
                            onClick={() => handleViewMyRFI(rfi)}
                            className="inline-flex w-full sm:w-auto items-center justify-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-md transition-colors"
                            style={{ backgroundColor: "#6036C4" }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#5028b0")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#6036C4")
                            }
                          >
                            View
                            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={handleDownload}
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-md transition-colors shadow-sm"
              style={{ backgroundColor: "#6036C4" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#5028b0")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#6036C4")
              }
              disabled={completeRfisCount === 0}
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Download All VQQs ({completeRfisCount})
            </button>
          </div>
        </div>
      </div>

      {/* Request Confirmation Dialog */}
      <Dialog
        open={showRequestDialog}
        onClose={() => setShowRequestDialog(false)}
        className="relative z-[60]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 backdrop-blur-sm transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
        />

        <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95"
            >
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <CheckCircleIcon
                    aria-hidden="true"
                    className="h-6 w-6 text-green-600"
                  />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold text-gray-900"
                  >
                    Confirm VQQ Request
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-4">
                      Are you sure you want to request this VQQ?
                    </p>

                    {requestingRfi && (
                      <div className="text-left bg-gray-50 rounded-md p-3">
                        <div className="flex items-center text-sm text-gray-700">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-3 flex-shrink-0"></span>
                          <div className="flex-1">
                            <span className="font-medium">
                              {requestingRfi.vendor}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              {requestingRfi.name}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={handleConfirmRequest}
                  disabled={isConfirming}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 disabled:opacity-50"
                >
                  {isConfirming ? "Requesting..." : "Confirm Request"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRequestDialog(false)}
                  disabled={isConfirming}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
};

// Request New Vendor Link
export const RequestNewVendorLink = ({ vendorName, websiteUrl, onClose }) => {
  const navigate = useNavigate();
};

// Vendor List Check Action - displays existing vendors and missing vendors from an uploaded list
export const VendorListCheckAction = ({
  existingVendors,
  missingVendors,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleViewProfile = (vendor) => {
    if (onClose) onClose();
    setTimeout(() => {
      navigate(`/provider/${vendor.id}/overview`);
    }, 100);
  };

  const handleRequestRFI = (vendorName) => {
    if (onClose) onClose();
    //TODO: adjust to populate vendorId and templateId
    setTimeout(() => {
      navigate(
        `/RFI/Create?vendorType=existing&vendorName=${encodeURIComponent(
          vendorName
        )}`
      );
    }, 100);
  };

  const handleRequestAssessment = (vendorName) => {
    if (onClose) onClose();
    //TODO: adjust to populate vendorId and templateId
    setTimeout(() => {
      navigate(
        `/Assessment/Create?vendorType=existing&vendorName=${encodeURIComponent(
          vendorName
        )}`
      );
    }, 100);
  };

  const handleRequestVendor = (vendorName, suggestedUrl) => {
    if (onClose) onClose();
    setTimeout(() => {
      navigate("/Provider/Create", {
        state: {
          preFilledData: {
            vendorName,
            websiteUrl: suggestedUrl,
          },
        },
      });
    }, 100);
  };

  return (
    <div className="space-y-4">
      {/* Existing Vendors Section */}
      {existingVendors && existingVendors.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">
            Available in Platform:
          </h4>
          {existingVendors.map((vendor, idx) => {
            const vendorType = vendor.type;
            const vendorTypeColor =
              vendorTypeHexColors[vendorType] || "#6B7280";

            return (
              <div
                key={idx}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 hover:border-[#6036C4] hover:shadow-lg transition-all group"
              >
                <div className="flex items-start gap-3">
                  {/* Logo */}
                  {vendor?.logo && (
                    <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
                      <img
                        src={vendor.logo}
                        alt={vendor.name}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    {/* Header: Name, Type, Location */}
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {vendor.name}
                      </h3>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ backgroundColor: vendorTypeColor }}
                        />
                        <span className="text-xs text-gray-600">
                          {vendorType}
                        </span>
                      </div>
                      {vendor.location && (
                        <div className="flex items-center gap-1">
                          <BuildingOfficeIcon className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs text-gray-600">
                            {vendor.location}
                          </span>
                        </div>
                      )}
                    </div>

                    <VendorServiceCategoryBadges
                      serviceCategories={vendor.serviceCategories}
                      highlightedServices={vendor.highlightedServices}
                      serviceColors={serviceColors}
                    />

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => handleViewProfile(vendor)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors shadow-sm"
                        style={{ backgroundColor: "#6036C4" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#5028b0")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = "#6036C4")
                        }
                      >
                        View Profile
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      </button>

                      <button
                        onClick={() => handleRequestRFI(vendor.name)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors shadow-sm"
                      >
                        Request VQQ
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                      </button>

                      <button
                        onClick={() => handleRequestAssessment(vendor.name)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors shadow-sm"
                      >
                        Request Assessment
                        <CheckBadgeIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Missing Vendors Section */}
      {missingVendors && missingVendors.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">
            Not Yet in Platform:
          </h4>
          <div className="space-y-2">
            {missingVendors.map((vendor, idx) => (
              <div
                key={idx}
                className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <BuildingOfficeIcon className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900">
                    {vendor.name}
                  </span>
                </div>
                <button
                  onClick={() =>
                    handleRequestVendor(vendor.name, vendor.suggestedUrl)
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors shadow-sm"
                >
                  Request New Vendor
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
